/**
 * Manages sorting functionality for the PS Table component.
 *
 * PSTableSorting handles column sorting with support for ascending/descending
 * order and toggling between sort states. It manages a single current sort field
 * and sort order, providing a clean interface for table column sorting.
 *
 * @class PSTableSorting
 * @example
 * // Create sorting with default settings
 * const sorting = new PSTableSorting();
 *
 * // Create sorting with custom options
 * const sorting = new PSTableSorting({
 *   enabled: true,
 *   sortfield: 'created_date',
 *   sortorder: 'DESC'
 * });
 *
 * // Disable sorting completely
 * const sorting = new PSTableSorting(false);
 */
class PSTableSorting {
	/**
	 * Creates a new PSTableSorting instance.
	 *
	 * @param {Object|boolean} options - Sorting configuration options
	 * @param {boolean} [options.enabled=true] - Whether sorting to include sorting UI elements on the table header
	 * @param {string} [options.sortfield] - The field name to sort by initially
	 * @param {string} [options.sortorder='ASC'] - The initial sort order ('ASC' or 'DESC')
	 */
	constructor(options) {
		// Handle boolean input by converting to object with enabled property
		if (options === false)
			options = { enabled: false };

		options = options ?? {};

		this.enabled = ko_helper.safe_observable(options.enabled, true);
		this.sortfield = ko_helper.safe_observable(options.sortfield, null);
		this.sortorder = ko_helper.safe_observable(options.sortorder, 'ASC');
	}

	/**
	 * Toggles the sort order for a given field.
	 *
	 * If the field is already the current sort field, it toggles between ASC and DESC.
	 * If it's a new field, it sets the sort order to ASC. The method respects the
	 * field's sortable property and the overall sorting enabled state.
	 *
	 * @param {Object|string} field - The field to sort by. Can be a field object with field() and sortable() methods, or a string field name
	 */
	toggle_sort(field) {
		let field_name = field;
		if (field.field instanceof Function)
		{
			field_name = field.field();
			if (!field.sortable())
				return;
		}

		if (!this.enabled())
			return;

		let order = 'ASC';
		if (field_name === this.sortfield())
		{
			order = this.sortorder() === 'ASC' ? 'DESC' : 'ASC';
		}

		this.sortfield(field_name);
		this.sortorder(order);
	}
}

export default PSTableSorting;
